import React, { useCallback, useState } from 'react';
import { UploadIcon } from './icons';
import { useTranslation } from '../i18n';

interface FileUploadProps {
  onFilesSelect: (files: File[]) => void;
  multiple: boolean;
  accept: string;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFilesSelect, multiple, accept }) => {
  const [isDragging, setIsDragging] = useState(false);
  const { t } = useTranslation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onFilesSelect(Array.from(e.target.files));
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const acceptedTypes = accept.split(',').map(type => type.trim());
      const acceptedFiles = Array.from(e.dataTransfer.files).filter((file: File) => {
        const fileExtension = `.${file.name.split('.').pop()}`;
        return acceptedTypes.some(type => {
          if (type.startsWith('.')) { // It's an extension like .pdf
            return fileExtension.toLowerCase() === type.toLowerCase();
          }
          if (type.endsWith('/*')) { // It's a wildcard like image/*
            return file.type.startsWith(type.slice(0, -1));
          }
          return file.type === type; // It's a specific MIME type like application/pdf
        });
      });

      if (acceptedFiles.length > 0) {
        onFilesSelect(acceptedFiles);
      }
      e.dataTransfer.clearData();
    }
  }, [onFilesSelect, accept]);

  const getFileTypeDescription = (acceptValue: string): string => {
    const lowerAccept = acceptValue.toLowerCase();
    const hasPdf = lowerAccept.includes('pdf');
    const hasImage = lowerAccept.includes('image') || lowerAccept.includes('jp') || lowerAccept.includes('png');
    const hasDoc = lowerAccept.includes('doc');

    if (hasPdf && hasDoc && hasImage) return t('fileTypeDescriptionAll');
    if (hasPdf && !hasImage && !hasDoc) return t('fileTypeDescriptionPdf');
    if (hasImage && !hasPdf && !hasDoc) return t('fileTypeDescriptionImage');
    
    const types: string[] = [];
    if (hasPdf) types.push('PDF');
    if (hasDoc) types.push('DOC/DOCX');
    if (hasImage) types.push('JPG/PNG');
    
    if (types.length > 0) {
      return `${t('fileTypeDescriptionAccepted')}: ${types.join(', ')}`;
    }
    
    return t('fileTypeDescriptionGeneric');
  };

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${isDragging ? 'border-indigo-500 bg-slate-700/50' : 'border-slate-600 bg-slate-800'}`}
    >
      <input
        type="file"
        multiple={multiple}
        accept={accept}
        onChange={handleFileChange}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        id="file-upload"
      />
      <label htmlFor="file-upload" className="flex flex-col items-center justify-center cursor-pointer">
        <UploadIcon />
        <p className="mt-4 text-lg font-semibold text-slate-200">
          {t('uploadTitle')}
        </p>
        <p className="mt-1 text-sm text-slate-400">
          {t('uploadSubtitlePre')} <span className="text-indigo-400 font-semibold">{t('uploadSubtitleLink')}</span>
        </p>
         <p className="mt-2 text-xs text-slate-500">
          {getFileTypeDescription(accept)}
        </p>
      </label>
    </div>
  );
};

export default FileUpload;
